import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter } from 'next/router';
import { CompanyProfileTabsWrapper }
  from '@fuww/library/src/CompanyProfile';
import { NavTabs } from '@fuww/library/src/Tabs';
import { useIntl } from 'react-intl';
import productExistenceQuery
  from '../../lib/queries/products/productExistenceQuery.graphql';
import Tab from '../Tab';
import withQuery from '../../lib/withQuery';
import messages from '../../lib/messages.json';
import { useSiteContext } from '../SiteContext';

const companyTabsPropertyTypes = {
  profile: PropTypes.shape({
    brands: PropTypes.arrayOf(PropTypes.shape({}).isRequired).isRequired,
    page: PropTypes.shape({
      premium: PropTypes.bool,
      title: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  data: PropTypes.shape({
    marketProducts: PropTypes.arrayOf(
      PropTypes.shape({}).isRequired,
    ).isRequired,
    marketProductsHidden: PropTypes.bool.isRequired,
    fashionFinderProducts: PropTypes.arrayOf(
      PropTypes.shape({}).isRequired,
    ).isRequired,
  }).isRequired,
};

type CompanyTabsProperties = InferProps<typeof companyTabsPropertyTypes>;

const CompanyTabs = ({ data, profile }: CompanyTabsProperties) => {
  const intl = useIntl();
  const { query: { slug }, route: currentRoute } = useRouter();
  const { marketProducts, marketProductsHidden, fashionFinderProducts } = data;
  const { brands, page: { premium } } = profile;

  const hasMarketProducts = brands.length > 0
    && (marketProducts.length > 0 || marketProductsHidden === true);
  const hasFashionFinderProducts = brands.length > 0
    && fashionFinderProducts.length > 0;

  const showProducts = premium && hasMarketProducts;
  const showFashionFinder = !showProducts && hasFashionFinderProducts;

  const tabs = [
    {
      route: 'company-profile',
      params: { slug },
      value: '/company-profile',
      label: intl.formatMessage(messages.about),
    },
    ...(showProducts ? [{
      route: 'company-profile-products',
      params: { slug },
      value: '/company-profile-products',
      label: intl.formatMessage(messages.products),
    }] : []),
    ...(showFashionFinder ? [{
      route: 'company-profile-fashion-finder',
      params: { slug },
      value: '/company-profile-fashion-finder',
      label: intl.formatMessage(messages['fashion-finder.title']),
    }] : []),
  ];

  if (tabs.length <= 1) return null;

  return (
    <CompanyProfileTabsWrapper>
      <NavTabs
        value={currentRoute}
        NavTab={Tab}
        tabs={tabs}
      />
    </CompanyProfileTabsWrapper>
  );
};

CompanyTabs.propTypes = companyTabsPropertyTypes;

const CompanyTabsWithQuery = withQuery(CompanyTabs);

const companyTabsWrapperPropertyTypes = {
  profile: PropTypes.shape({
    brands: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired).isRequired,
  }).isRequired,
};

type CompanyTabsWrapperProperties = InferProps<
  typeof companyTabsWrapperPropertyTypes
>;

const CompanyTabsWrapper = ({
  profile,
}: CompanyTabsWrapperProperties) => {
  const { locale } = useSiteContext();

  return (
    <CompanyTabsWithQuery
      profile={profile}
      query={productExistenceQuery}
      variables={{
        brandIds: profile.brands.map(({ id }) => id),
        locales: [locale],
      }}
      errorMessage="Error loading company tab"
    />
  );
};

CompanyTabsWrapper.propTypes = companyTabsWrapperPropertyTypes;

const schoolTabsPropertyTypes = {
  profile: PropTypes.shape({
    coursesConnection: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({}).isRequired).isRequired,
    }).isRequired,
  }).isRequired,
};

type SchoolTabsProperties = InferProps<typeof schoolTabsPropertyTypes>;

const SchoolTabs = ({ profile }: SchoolTabsProperties) => {
  const intl = useIntl();
  const { query: { slug }, route: currentRoute } = useRouter();
  const { coursesConnection: { edges } } = profile;

  return (
    <CompanyProfileTabsWrapper>
      {edges.length > 0 && (
      <NavTabs
        value={currentRoute}
        NavTab={Tab}
        tabs={[
          {
            route: 'company-profile',
            params: { slug },
            value: '/company-profile',
            label: intl.formatMessage(messages.about),
          },
          {
            route: 'school-profile-courses',
            params: { slug },
            value: '/school-profile-courses',
            label: 'courses',
          },
        ]}
      />
      )}
    </CompanyProfileTabsWrapper>
  );
};

SchoolTabs.propTypes = schoolTabsPropertyTypes;

export { SchoolTabs, CompanyTabsWrapper };
